/**
 * Rendering for quality-check metadata — the severity badge and the ⓘ that
 * explains what a check means.
 *
 * The catalogue itself (`QUALITY_CHECK_META`) lives in `@padcanvas/shared`
 * alongside the check-name union, because the backend's escalation policy
 * partitions on the same `escalation` field. Here we only decide how it looks.
 *
 * Names arrive from the API as plain strings — a row persisted by a branch that
 * knows a check this build doesn't will simply render without metadata.
 */
import { QUALITY_CHECK_META, type QualityCheckName, type QualityCheckMeta, type QualityCheckSeverity } from '@padcanvas/shared';

export function checkMeta(name: string): QualityCheckMeta | undefined {
  return QUALITY_CHECK_META[name as QualityCheckName];
}

/** Sort weight: worst first under a descending sort. Unknown checks sort last. */
export const SEVERITY_RANK: Record<QualityCheckSeverity, number> = { high: 3, medium: 2, low: 1 };
export function severityRank(name: string): number {
  const meta = checkMeta(name);
  return meta ? SEVERITY_RANK[meta.severity] : 0;
}

const SEVERITY_CLASS: Record<QualityCheckSeverity, string> = {
  high: 'qc-sev--high', medium: 'qc-sev--medium', low: 'qc-sev--low',
};

/** What a firing costs, not how often it happens — see QualityCheckSeverity. */
export function SeverityBadge({ name }: { name: string }) {
  const meta = checkMeta(name);
  if (!meta) return <span className="muted">—</span>;
  return <span className={`qc-sev ${SEVERITY_CLASS[meta.severity]}`}>{meta.severity}</span>;
}

const ESCALATION_TEXT: Record<QualityCheckMeta['escalation'], string> = {
  mechanical: 'Escalates on every path — a stronger model reliably fixes this.',
  reading: 'Escalates on every path — the page may have been misread, and a stronger model re-reads it.',
  judgment: 'Escalates only where no student is waiting (admin re-evaluate, evals).',
  informational: 'Never escalates — re-running at a stronger model would not resolve it.',
};

/**
 * The check's short description, with the long form collapsed beneath it.
 *
 * Two registers, deliberately: the description says what tripped and is what you
 * read while triaging; `details` says where each side of the comparison comes
 * from and which one is authoritative, which you need once — when deciding
 * whether a firing is a real defect. Putting the second inline would push the
 * firings themselves below the fold.
 *
 * Native `<details>` rather than a state hook: it is keyboard- and
 * screen-reader-accessible for free, and browser find-in-page can open it.
 * Renders nothing at all for a check with no metadata.
 */
export function CheckDescription({ name }: { name: string }) {
  const meta = checkMeta(name);
  if (!meta) return null;
  return (
    <>
      <p className="muted">{meta.description}</p>
      {/* `!!` matters: a bare `length &&` would render a literal 0. */}
      {!!meta.details?.length && (
        <details className="qc-details">
          <summary>How this check works</summary>
          {meta.details.map((para, i) => <p key={i} className="muted">{para}</p>)}
          <p className="muted">{ESCALATION_TEXT[meta.escalation]}</p>
        </details>
      )}
    </>
  );
}

/**
 * Hover/focus card carrying the check's description. CSS-only (no portal): the
 * table row is not scroll-clipped, so a positioned child is enough, and it stays
 * keyboard-reachable as a real button.
 */
export function CheckInfo({ name }: { name: string }) {
  const meta = checkMeta(name);
  if (!meta) return null;
  return (
    <span className="qc-info">
      <button type="button" className="qc-info-btn" aria-label={`What ${name} means`}>ⓘ</button>
      <span className="qc-tip" role="tooltip">
        <span className="qc-tip-name">{name}</span>
        <span className="qc-tip-body">{meta.description}</span>
        <span className="qc-tip-esc">{ESCALATION_TEXT[meta.escalation]}</span>
      </span>
    </span>
  );
}
